import React from 'react'
import mainHeaderImage from 'assets/images/about-us-header.jpg'
import ourPhilosophyImage from 'assets/images/our-philosophy-image.png'
import foundersPhoto from 'assets/images/Founders1.png'
import { QuoteLeftIcon, QuoteRightIcon } from 'components/icons'
import BecomeAMemberSection from 'components/sections/BecomeAMemberSection'
import whyUs1 from 'assets/images/why-us-1.png'
import whyUs2 from 'assets/images/why-us-2.png'
import whyUs3 from 'assets/images/why-us-3.png'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import { useScreenWidth } from 'data/hooks/useScreenWidth'
import { config } from 'data/config'
import Image from 'components/Image'
import InformationSection from 'components/sections/InformationSection'
import PublicLayout, { PublicLayoutProps } from 'layouts/PublicLayout'
import { ReactChild } from 'data/types/types'
import { useHasScrolled } from 'data/hooks/useHasScrolled'
import { defaultCarouselProps } from '@ama-selections/ui'

const AboutUsSlide = ({ image, content }: {
  image: string,
  content: {
    subtitle: string,
    title: string,
    content: ReactChild,
  },
}) => {
  return (
    <div className="flex flex-col items-center justify-center w-screen h-full px-20 pt-40 md:flex-row gap-x-50 gap-y-20">
      <div className="h-[400px] relative w-full">
        <Image
          src={image}
          className="object-cover bg-transparent shadow-image-15 rounded-4"
          layout="fill"
        />
      </div>

      <div className="w-full max-w-[400px] pt-10 gap-20 flex flex-col md:pr-30">
        <div className="font-bold text-primary text-14 leading-20 tracking-1/2 opacity-80">
          {content.subtitle}
        </div>
        <div className="font-serif font-bold font-grey-1000 text-32 md:text-36 leading-title tracking-title">
          {content.title}
        </div>
        <div className="pb-20 font-normal text-grey-750 text-16 leading-26">
          {content.content}
        </div>
      </div>
    </div>
  )
}

const AboutUs = () => {
  const { width } = useScreenWidth()
  const hasScrolled = useHasScrolled(0, true)

  const navigationPrevRef = React.useRef<HTMLDivElement>(null)
  const navigationNextRef = React.useRef<HTMLDivElement>(null)
  const buttonClasses = 'absolute visible !w-40 !h-40 sm:!w-50 sm:!h-50 top-[230px] sm:top-[345px] md:top-1/2 -mt-20 md:-mt-25'

  const seo: PublicLayoutProps['seo'] = {
    title: 'A.M.A Selections Offer Luxury Holiday Villa Rentals in France, Italy & Spain',
    description: 'A.M.A Selections is a luxury villa rental company aiming to offer a collection of the finest holiday villas for rent on the French Riviera, Italy & Spain.',
    openGraph: {
      type: 'business',
      url: config.appURL + '/about-us',
    },
  }

  return (
    <PublicLayout
      seo={seo}
      mobileNavBar={{
        visible: hasScrolled,
        back: true,
        profileTrigger: true,
      }}
      className={{
        content: '!mt-0',
      }}
    >
      <div className="relative flex h-full min-h-[375px] lg:min-h-[790px]">
        <Image
          src={mainHeaderImage}
          alt="Beautiful blue sea bay with white walled building"
          className="absolute inset-0 object-cover"
          layout="fill"
          priority
        />
        <h1 className="absolute max-w-[993px] mx-auto left-0 right-0 bottom-[44%] text-center text-white font-bold font-serif text-32 md:text-42 lg:text-48 xl:text-60 leading-title tracking-title drop-shadow-title">
          {
            width > 790
              ? 'Get to know our story, our philosophy, and why you should book with us'
              : 'About Us'
          }
        </h1>
      </div>

      <div className="relative -mt-20 antialiased bg-white md:static container-public my-50 lg:my-150 rounded-t-20">
        <InformationSection
          subtitle="Our Story"
          title="A passion for quality & service coupled with a drive to innovate"
          text={[
            'Founded by Andrea Saliu and Mariek Anselme, A.M.A Selections began as a local boutique holiday rental company on the French Riviera. With just over 15 properties, the company launched in 2014 with the sole purpose of delivering an exceptional experience while inspiring guests to discover the region.',
            'A.M.A Selections has since established itself at the forefront of the luxury vacation rental industry. With more than 20 years of combined experience and one of the highest average client spend in the industry, we know how to fullfil the expectations of the most discerning clientele.',
            'Today, A.M.A Selections offers a collection of the world’s most exceptional holiday homes complemented with an innovative high-tech, high-touch approach. No matter the growth, our company purpose remains the same and continues to guide the work we do everyday on behalf of our guests.',
          ]}
          image={{
            url: foundersPhoto,
            altText: 'A photo of the founders of A.M.A Selections, Andrea Saliu and Mariek Anselme.',
          }}
        />
      </div>

      <div className="bg-grey-100 w-full flex items-center justify-center flex-col gap-50 py-75 md:py-150 xl:py-[150px] mb-40 md:mb-0">
        <div className="text-30 lg:text-[56px] font-normal font-serif max-w-[90rem] text-grey-1000 text-center leading-[40px] lg:leading-[76px] tracking-[-1px] px-20 whitespace-pre-line">
          <div className="">
            <QuoteRightIcon className="scale-[0.6] lg:scale-[0.81]" />

          </div>
          <p className="mx-auto max-w-[300px] xs:max-w-full xs:mx-25 mt-25">
            By combining expert curation, hands-on service and proprietary technology, we have reimagined a booking experience tailored to the luxury traveler.
          </p>
          <div className="flex justify-end scale-[0.75] lg:scale-100 right-12 absolute lg:static">
            <QuoteLeftIcon />
          </div>
        </div>

        <p className="italic text-grey-750 text-16 leading-22">
          Mariek Anselme, Cofounder
        </p>
      </div>


      <InformationSection
        subtitle="Our Philosophy"
        title="It all begins by connecting with you"
        text={[
          'We’ve mastered the art of making luxury holiday rentals beautifully personal. It all begins by connecting with you and discovering your likes, loves and discerning tastes.',
          'From traditional countryside farmhouses to grand seafront estates and bespoke services, we carefully curate the very best in each destination to provide an all-encompassing service from beginning to end.',
          'In a digitally driven world, we believe that your travel experience begins the moment you arrive on our site. Through a relentless desire to exceed the expectations of our guests, and by combining expert curation, hands-on service and proprietary technology, we have reimagined a booking experience that is tailored to the luxury traveler.',
        ]}
        image={{
          url: ourPhilosophyImage,
          altText: 'A stone patio with wicker furniture and trees.',
        }}
      />

      <hr className="hidden text-opacity-25 text-grey-300 my-150 lg:block" />

      <div className="text-grey-900 font-serif font-bold lg:font-normal text-32 lg:text-[48px] leading-title tracking-title text-center">
        Why Book With Us
      </div>

      <div className="mt-50 md:mt-90 pb-30 md:pb-0 h-full max-h-[900px] xl:hidden">
        <Swiper
          {...defaultCarouselProps}
          modules={[Navigation]}
          slidesPerView={1}
          spaceBetween={40}
          navigation={{
            prevEl: navigationPrevRef.current,
            nextEl: navigationNextRef.current,
          }}
          onSwiper={(swiper: SwiperClass) => {
            setTimeout(() => {
              // Timeout until prevEl & nextEl refs are defined
              if (swiper.params.navigation && typeof (swiper.params.navigation) !== 'boolean') {
                swiper.params.navigation.prevEl = navigationPrevRef.current
                swiper.params.navigation.nextEl = navigationNextRef.current
              }

              // Re-initialise navigation
              swiper.navigation.destroy()
              swiper.navigation.init()
              swiper.navigation.update()
            })
          }}
          observer={true}
        >
          <SwiperSlide>
            <AboutUsSlide
              image={whyUs1.src}
              content={{
                subtitle: 'THE WORLD’S BEST HOLIDAY HOMES',
                title: 'Carefully curated by us. Exceptional homes for you.',
                content: <p>
                  Unlike mass booking platforms, we select only the top 1% of verified homes in the world’s most fashionable destinations.
                  <br /><br />
                  Based on your criteria, our team can assist you in your search and send a personalized selection of homes that best match your preferences and occasion.
                </p>,
              }}
            />
          </SwiperSlide>

          <SwiperSlide>
            <AboutUsSlide
              image={whyUs2.src}
              content={{
                subtitle: 'BESPOKE BOOKING EXPERIENCE',
                title: 'A tailored, cutting-edge solution for the luxury traveler.',
                content: <p>
                  Once your booking is confirmed, our in-house concierge team will assist you in tailoring your stay to create an unforgettable holiday.
                  <br /><br />
                  Our industry leading high-tech approach allows you to seamlessly request services, recommendations, share your trip itinerary and more, directly from your account.
                </p>,
              }}
            />
          </SwiperSlide>

          <SwiperSlide>
            <AboutUsSlide
              image={whyUs3.src}
              content={{
                subtitle: 'LOCAL PROFESSIONAL ASSISTANCE',
                title: 'Rigorously vetting the very best local partners.',
                content: <p>
                  We are solving the problem of inconsistent quality by vetting and working with the very best property owners and local home management companies that adhere to our strict quality standards.
                  <br /><br />
                  This provides you with reliable and professional on-site assistance throughout your stay.
                </p>,
              }}
            />
          </SwiperSlide>

          <div ref={navigationPrevRef} className={`swiper-button-prev left-10 lg:left-15 xl:left-20 ${buttonClasses}`}></div>
          <div ref={navigationNextRef} className={`swiper-button-next right-10 lg:right-15 xl:right-20 ${buttonClasses}`}></div>
        </Swiper>
      </div>

      <div className="max-w-6xl mx-auto w-full py-[150px] xl:flex gap-90 justify-between lg:flex-row flex-col hidden">
        <div className="relative w-full max-w-[540px] mx-auto h-[660px] shadow-image">
          <Image src={whyUs1} className="rounded-4" layout="fill" />
        </div>

        <div className="max-w-[480px] h-full justify-evenly pt-10 gap-20 flex flex-col my-auto">
          <div className="font-bold text-primary text-14 leading-20 tracking-1/2 opacity-80">
            THE WORLD’S BEST HOLIDAY HOMES
          </div>
          <div className="font-serif font-bold font-grey-1000 text-36 leading-title tracking-title">
            Carefully curated by us. Exceptional homes for you.
          </div>
          <div className="mt-5 font-normal text-grey-700 text-16 leading-26">
            Unlike mass booking platforms, we select only the top 1% of verified homes in the world’s most fashionable destinations.<br /><br />

            Based on your criteria, our team can assist you in your search and send a personalized selection of homes that best match your preferences and occasion.

          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto w-full pb-[150px] xl:flex gap-90 hidden justify-between">
        <div className="max-w-[480px] h-full justify-evenly pt-10 gap-20 flex flex-col my-auto">
          <div className="font-bold text-primary text-14 leading-20 tracking-1/2 opacity-80">
            BESPOKE BOOKING EXPERIENCE
          </div>
          <div className="font-serif font-bold font-grey-1000 text-36 leading-title tracking-title">
            A tailored, cutting-edge solution for the luxury traveler.
          </div>
          <div className="mt-5 font-normal text-grey-750 text-16 leading-26">
            Once your booking is confirmed, our in-house concierge team will assist you in tailoring your stay to create an unforgettable holiday. <br /><br />

            Our industry leading high-tech approach allows you to seamlessly request services, recommendations, share your trip itinerary and more, directly from your account.

          </div>
        </div>

        <div className="relative w-full max-w-[540px] mx-auto h-[660px] shadow-image-right">
          <Image src={whyUs2} className="rounded-4" layout="fill" />
        </div>
      </div>

      <div className="max-w-6xl mx-auto w-full pb-0 hidden xl:flex gap-90 justify-between">
        <div className="relative w-full max-w-[540px] mx-auto h-[660px] shadow-image">
          <Image src={whyUs3} className="rounded-4" layout="fill" />
        </div>

        <div className="max-w-[480px] h-full justify-evenly pt-10 gap-20 flex flex-col my-auto">
          <div className="font-bold text-primary text-14 leading-20 tracking-1/2 opacity-80">
            LOCAL PROFESSIONAL ASSISTANCE
          </div>
          <div className="font-serif font-bold font-grey-1000 text-36 leading-title tracking-title">
            Rigorously vetting the very best local partners.
          </div>
          <div className="mt-5 font-normal text-grey-750 text-16 leading-26">
            We are solving the problem of inconsistent quality by vetting and working with the very best property owners and local home management companies that adhere to our strict quality standards. <br /><br />

            This provides you with reliable and professional on-site assistance throughout your stay.
          </div>
        </div>
      </div>

      <BecomeAMemberSection />
    </PublicLayout >
  )
}

export default AboutUs
